import classNames from "classnames";
import LogsConstants from "fleet/constant/logs";
import countBy from "lodash/countBy";
import filter from "lodash/filter";
import get from "lodash/get";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import pick from "lodash/pick";
import startsWith from "lodash/startsWith";
import uniq from "lodash/uniq";
import uniqWith from "lodash/uniqWith";
import uniqueId from "lodash/uniqueId";
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import { FormText } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Datetime from "react-datetime";
import ReactJson from "react-json-view";
import Select from "react-select";
import * as App from "redux-store/applications/selectors";
import semverGte from "semver/functions/gte";
import semverValid from "semver/functions/valid";
import { Collapsible } from "ui/collapsible";
import {
  Filter,
  FILTER_TYPE_SELECT,
  FILTER_TYPE_MULTI_SELECT,
} from "ui/filter";
import { Notification } from "ui/notification";
import { Table, Column } from "ui/table";
import { transformValues } from "util/object";
import { getErrorMessage } from "util/response";
import { utcToLocalFormat } from "util/time";

function AppProcessingLabel(props) {
  const { extra } = props;
  const icon =
    extra && !isEmpty(extra.failures)
      ? "fa fa-times text-danger"
      : "fa fa-check text-success";

  const status =
    extra && !isEmpty(extra.failures)
      ? LogsConstants.APP_FAILURE_DETECTED
      : LogsConstants.APP_FAILURE_NOT_DETECTED;

  return (
    <div className="app-failure-status">
      <i className={icon} />
      <span className="row-message">{status}</span>
    </div>
  );
}

AppProcessingLabel.propTypes = {
  extra: PropTypes.object.isRequired,
};

function CollapsibleMessage(props) {
  const { extra, message } = props;
  const helpText = get(LogsConstants.messages, message, null);

  return (
    <div>
      <Collapsible label={<MessageLabel extra={extra} message={message} />}>
        <div className="formatted-log">
          {helpText && (
            <div className="help-message-wrapper">
              <i className="fa fa-info-circle" />
              <span className="row-message">{helpText}</span>
            </div>
          )}
          {!isEmpty(extra) && (
            <ReactJson
              collapseStringsAfterLength={false}
              displayDataTypes={false}
              displayObjectSize={true}
              enableAdd={false}
              enableClipboard={true}
              enableDelete={false}
              enableEdit={false}
              iconStyle="square"
              indentWidth={1}
              name={false}
              quotesOnKeys={false}
              src={extra}
              theme="apathy:inverted"
            />
          )}
        </div>
      </Collapsible>
    </div>
  );
}

CollapsibleMessage.propTypes = {
  extra: PropTypes.object.isRequired,
  message: PropTypes.string.isRequired,
};

function MessageLabel(props) {
  const { extra, message } = props;

  if (_isAppProcessingEntry(message)) {
    return (
      <div className="message-label">
        <span className="app-failure-label">{message} </span>
        <AppProcessingLabel extra={extra} />
      </div>
    );
  }

  return <span className="message-label">{message}</span>;
}

MessageLabel.propTypes = {
  extra: PropTypes.object.isRequired,
  message: PropTypes.string.isRequired,
};

function _isAppProcessingEntry(message) {
  return LogsConstants.APP_FAILURE_MESSAGES.includes(message);
}

function _mapper(value) {
  try {
    return JSON.parse(value.replace(/\\/g, ""));
  } catch (e) {
    return value;
  }
}

class DeviceLogsPage extends React.Component {
  static get propTypes() {
    return {
      applications: PropTypes.object.isRequired,
      device: PropTypes.object.isRequired,
      fetchDeviceLogs: PropTypes.func.isRequired,
      oldLogsRangeSeconds: PropTypes.number,
      refreshSeconds: PropTypes.number,
      storesLoaded: PropTypes.bool.isRequired,
    };
  }

  static get defaultProps() {
    return {
      refreshSeconds: 10,
      oldLogsRangeSeconds: 60 * 60,
    };
  }

  constructor(props) {
    super(props);

    this.tableOptions = {
      sortName: "id",
      sortOrder: "desc",
      sizePerPage: 25,
    };

    this.filterApi = null;
    this._getFilterApi = this._getFilterApi.bind(this);
    this._onFilterChange = this._onFilterChange.bind(this);

    this.state = {
      lines: [],
      level: "all",
      timer: null,
      error: "",
      first: moment().unix(),
      datepickerStart: (moment().unix() - 60 * 60) * 1000,
      datepickerDuration: 1,
      last: null,
      refreshing: false,
      selectedTags: [],
    };

    this._formatMessage = this._formatMessage.bind(this);
    this._formatTag = this._formatTag.bind(this);
    this._onTagSelect = this._onTagSelect.bind(this);
    this._updateLogLevel = this._updateLogLevel.bind(this);
  }

  componentDidUpdate(_prevProps) {
    const { device, refreshSeconds } = this.props;
  }

  componentDidMount() {
    const { device, refreshSeconds } = this.props;
  }

  componentWillUnmount() {
    if (this.state.timer) {
      clearInterval(this.state.timer);
    }
  }

  _formatMessage(message, row) {
    if (!isEmpty(row.extra)) {
      const hasFailures = !isEmpty(row.extra.failures);
      const isAppEntry = _isAppProcessingEntry(message);
      const formattedExtra = transformValues(row.extra, _mapper);

      if (!isAppEntry) {
        return <CollapsibleMessage extra={formattedExtra} message={message} />;
      } else if (hasFailures) {
        return <CollapsibleMessage extra={formattedExtra} message={message} />;
      }
    }

    return <MessageLabel extra={row.extra} message={message} />;
  }

  _formatTimestamp(timestamp) {
    return utcToLocalFormat(timestamp, "MMM Do, YYYY hh:mm:ss a");
  }

  _formatTag(tag) {
    const { applications } = this.props;

    if (!tag) {
      return "N/A";
    }

    if (tag.startsWith("app.")) {
      const appName = tag.substring(4);
      const app = App.getByApplicationName(applications, appName);

      if (app) {
        return App.name(app);
      }
    }

    return tag;
  }

  _getCommonErrors() {
    // Filter all warnings and errors
    // Only care about errors from applications, starting with tag="app.*"
    const filteredErrors = filter(this.state.lines, (line) => {
      return (
        includes(["warn", "error"], line.level) && startsWith(line.tag, "app.")
      );
    });

    // Count all errors by message
    const errorsCount = countBy(filteredErrors, "message");

    // Pick the ones that occur more than once
    return pick(errorsCount, (k, v) => k > 1);
  }

  _getLogLines() {
    const selectedTagValues = this._getSelectedTagValues();

    // Return all lines by default
    let lines = this.state.lines;
    lines.forEach((line, index) => {
      line.id = index + 1;
    });

    // If there are selected tags, filter by those
    if (selectedTagValues.length > 0) {
      lines = filter(lines, (line) => {
        return includes(selectedTagValues, line.tag);
      });
    }

    // Filter by log level: all, info and warning
    if (this.state.level === "all") {
      return lines;
    }

    return filter(lines, (line) => {
      return line.level === this.state.level;
    });
  }

  _getSelectedTagValues() {
    return map(this.state.selectedTags, (l) => l.value);
  }

  _goRetrieveLogs(device, level, start, end) {
    const { fetchDeviceLogs } = this.props;
    const serviceArgs = {
      level,
      start,
      end,
      deviceId: device.id,
    };

    return fetchDeviceLogs(serviceArgs)
      .then((res) => {
        const { data = [] } = res.data;
        const lines = this._orderLines(data);

        this.setState({
          lines,
          first: start < this.state.first ? start : this.state.first,
          last: end > this.state.last ? end : this.state.last,
          error: "",
        });
      })
      .catch((e) => {
        this.setState({
          error: `Failed to retrieve device logs. Your device might be
            offline or not supporting this feature yet. Server reply:
            ${getErrorMessage(e)}`,
        });
      })
      .finally(() => {
        this.setState({
          refreshing: false,
        });
      });
  }

  _orderLines(lines) {
    lines = lines || [];
    lines = orderBy(lines, ["timestamp"], ["asc"]);
    return uniqWith(lines, isEqual);
  }

  _onTagSelect(lst) {
    lst = lst || [];
    this.setState({ selectedTags: lst });
  }

  _renderCommonErrors() {
    const commonErrors = this._getCommonErrors();

    if (isEmpty(commonErrors)) {
      return null;
    }

    return (
      <Row>
        <Col xs={12}>
          <h4>Common Messages</h4>
          <ul>
            {map(commonErrors, (value, key) => {
              return (
                <li key={uniqueId("common-error-")}>
                  {key} ({value} times)
                </li>
              );
            })}
          </ul>
        </Col>
      </Row>
    );
  }

  _handleDatepickerChange(d) {
    if (!d || typeof d.unix !== "function") {
      return;
    }
    this.setState({ datepickerStart: d.unix() * 1000 });
  }

  _clickedFetchLogs() {
    const start = Math.floor(this.state.datepickerStart / 1000);
    const end = start + this.state.datepickerDuration * 60 * 60;
    this._retrieveLogs(this.props.device, this.state.level, start, end);
  }

  _handleDurationChange(selectedOption) {
    this.setState({
      datepickerDuration: selectedOption ? selectedOption.value : null,
    });
  }

  _logFileName(device) {
    const start = Math.floor(this.state.datepickerStart / 1000);
    const end = start + this.state.datepickerDuration * 60 * 60;
    return `cortex-device-${device.device_id}-${start}-${end}.log`;
  }

  _isValidDate(current) {
    const now = moment();
    const weekAgo = moment().subtract(7, "days");
    return current.isBetween(weekAgo, now, "day", "[]");
  }

  _renderDatePickerTimeSelect(device) {
    const allow_extended_logs =
      semverValid(device.current_version) &&
      semverGte(
        device.current_version,
        LogsConstants.MIN_VER_FOR_EXTENDED_LOGS,
      );

    const options_1hour = [{ value: 1, label: "1 Hour" }];
    const options_4hour = [
      { value: 1, label: "1 Hour" },
      { value: 2, label: "2 Hour" },
      { value: 4, label: "4 Hour" },
    ];
    const log_hours = allow_extended_logs ? "1, 2 or 4 hours" : "1 hour";
    const options = allow_extended_logs ? options_4hour : options_1hour;

    return (
      <Row style={{ marginBottom: "50px" }}>
        <Col xs={2}>
          <label>Pick a start date and time</label>
          <Datetime
            value={this.state.datepickerStart}
            className="deploy-datepicker"
            inputProps={{
              placeholder: "Select start date.",
              readOnly: false,
            }}
            label="Test label"
            timeFormat={true}
            isValidDate={this._isValidDate}
            onChange={(d) => this._handleDatepickerChange(d)}
          />
        </Col>
        <Col xs={3}>
          <label>Log Duration</label>
          <Select
            name="duration-select"
            onChange={(d) => this._handleDurationChange(d)}
            options={options}
            value={options.find(
              (option) => option.value === this.state.datepickerDuration,
            )}
            required
          />
          <FormText>
            {`You can download ${log_hours} of logs at a time.`}
          </FormText>
        </Col>
        <Col xs={3}>
          <label style={{ display: "block" }}>&nbsp;</label>
          <Button
            variant="primary"
            onClick={() => this._clickedFetchLogs()}
            style={{ marginRight: "26px" }}
          >
            <i className={"fa fa-eye"} />
            &nbsp;Retrieve Logs
          </Button>
          &nbsp;
          <a
            className="btn btn-primary"
            href={this._extractDeviceLogsJson()}
            download={this._logFileName(device)}
            disabled={this.state.lines.length === 0}
            onClick={(event) => {
              if (this.state.lines.length === 0) {
                event.preventDefault();
              }
            }}
            style={{
              pointerEvents: this.state.lines.length === 0 ? "none" : "auto",
            }}
            target={"_blank"}
          >
            <i className={"fa fa-download"} />
            &nbsp;Download Logs
          </a>
          &nbsp;
        </Col>
      </Row>
    );
  }

  _retrieveLogs(device, level, start, end) {
    this.setState(
      { refreshing: true },
      this._goRetrieveLogs.bind(this, device, level, start, end),
    );
  }

  _updateLogLevel(level) {
    this.setState({ level: level });
  }

  _extractDeviceLogsJson() {
    const logData = this._getLogLines();
    return window.URL.createObjectURL(
      new window.Blob([logData.map((obj) => JSON.stringify(obj)).join("\n")], {
        type: "application/json",
      }),
    );
  }

  _extractDeviceLogs() {
    const headers = ["Timestamp", "Tag(s)", "Log Level", "Message"];

    const logData = this._getLogLines().map((logLine) => {
      return [logLine.timestamp, logLine.tag, logLine.level, logLine.message];
    });

    return [headers, ...logData];
  }

  _onFilterChange(filterState) {
    const { activeFilters } = filterState;

    // Reset state back to unselected
    this.setState({ level: "all" });
    this.setState({ selectedTags: [] });

    activeFilters.forEach((f) => {
      switch (f.name) {
        case "log_level":
          this.setState({ level: f.value });
          break;
        case "tags":
          this.setState({ selectedTags: f.value });
          break;
      }
    });
  }

  _getFilterApi() {
    this.filterApi = filterApi;
  }

  _renderTableToolbar(tagOptions) {
    const filterFields = [
      {
        label: "Log Level",
        name: "log_level",
        placeholder: "Log Levels",
        type: FILTER_TYPE_SELECT,
        options: [
          {
            label: "Debug",
            name: "debug",
            placeholder: "Debug",
            value: "debug",
          },
          { label: "Info", name: "info", placeholder: "Info", value: "info" },
          { label: "Warn", name: "warn", placeholder: "Warn", value: "warn" },
          {
            label: "Error",
            name: "error",
            placeholder: "Error",
            value: "error",
          },
        ],
      },
      {
        label: "Tag",
        name: "tags",
        placeholder: "Tag",
        type: FILTER_TYPE_MULTI_SELECT,
        options: tagOptions,
      },
    ];

    return (
      <Filter
        onFilterChange={this._onFilterChange}
        fields={filterFields}
        getApi={this.getFilterApi}
      />
    );
  }

  render() {
    const { device, refreshSeconds, storesLoaded } = this.props;
    const { error, lines, refreshing, selectedTags } = this.state;

    if (!storesLoaded || !device) {
      return null;
    }

    const refreshClasses = classNames({
      "pull-right": true,
      "text-blink": true,
      label: true,
      "bg-maroon": true,
      invisible: !refreshing,
    });

    const uniqueTags = uniq(filter(map(lines, "tag")));

    const tagOptions = map(uniqueTags, (tag) => {
      return { name: tag, label: tag, placeholder: tag, value: tag };
    });

    return (
      <Row>
        <Col xs={12}>
          <Row>
            <Col xs={12}>{this._renderDatePickerTimeSelect(device)}</Col>
          </Row>
          <Row>
            <Col xs={6}></Col>
            <Col xs={6}>
              <div className="refreshing-wrapper">
                <span className={refreshClasses}>Refreshing</span>
              </div>
            </Col>
          </Row>

          {this._renderCommonErrors()}
          <Notification error={error} />

          <Table
            data={this._getLogLines()}
            hover={true}
            pagination={true}
            search={true}
            options={this.tableOptions}
            toolbar={this._renderTableToolbar(tagOptions)}
          >
            <Column key="id" dataField="id" isKey={true} hidden={true}>
              ID
            </Column>
            <Column
              key="timestamp"
              dataField="timestamp"
              dataSort={true}
              width="180px"
              dataFormat={this._formatTimestamp}
            >
              Timestamp
            </Column>
            <Column
              key="tag"
              width="250px"
              dataField="tag"
              dataFormat={this._formatTag}
            >
              Tag
            </Column>
            <Column key="level" width="80px" dataField="level">
              Level
            </Column>
            <Column
              key="message"
              dataField="message"
              dataSort={true}
              dataFormat={this._formatMessage}
            >
              Message
            </Column>
          </Table>
        </Col>
      </Row>
    );
  }
}

export { AppProcessingLabel, CollapsibleMessage, DeviceLogsPage, MessageLabel };
